import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens, Tooltip } from '@hotjar/hotsauce';

import { useSettings } from '@hotjar/webapp/hooks/useSettings';
import { useSitePlans } from '@hotjar/webapp/hooks/useSitePlans';
import type { SiteDTO } from '@hotjar/site';
import { BASIC } from '@hotjar/webapp/domains/revenue/plan/feature-group';
import { mapSiteDtoToModel, isPlanLocked } from '@hotjar/site';
import type { OwnedAccount } from '@hotjar/account';

import { useHelpHotjarUrl } from '@hotjar/translations';
import { SiteLockedIcon } from './SiteLockedIcon';
import { DetailLabel } from './components/DetailLabel';
import { DetailLabelIcon } from './components/DetailLabelIcon';
import { DetailContainer } from './components/DetailContainer';
import { DetailSize } from './components/DetailSize';

const SNAPSHOT_SIZE = 'playbacks.snapshot_size';
const PLAYBACKS = 'playbacks';

interface Props {
  account: OwnedAccount;
  site: SiteDTO;
  children: ReactElement;
}

export const SiteDetailsSnapshots = ({ account, site, children }: Props): JSX.Element => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const { plan } = useSitePlans(site);
  const { data: settings } = useSettings();

  const siteLocked = isPlanLocked(mapSiteDtoToModel(site), account);

  const snapshotSize = settings?.plans[plan.featureGroup].limits[SNAPSHOT_SIZE];

  return (
    <DetailContainer>
      {children}

      <Tooltip text={t('billing:site-box.snapshot-tooltip')}>
        <a
          color={tokens.color.text_light}
          target="_blank"
          href={`${helpHotjarUrl}/articles/360012620453`}
          rel="noopener noreferrer"
        >
          <DetailLabel>{t('billing:site-box.snapshot-recordings')}</DetailLabel>
          <DetailLabelIcon icon="info_alt" />
        </a>
      </Tooltip>

      <DetailSize>
        {t('billing:site-box.snapshot-size', {
          size: snapshotSize,
          max: plan.featureGroup === BASIC && settings?.plans.basic.limits[PLAYBACKS],
        })}
        {siteLocked && <SiteLockedIcon />}
      </DetailSize>
    </DetailContainer>
  );
};
